import { useMemo, useCallback } from "react";

const PROGRAMS_ALIAS = {
	CSI: "customerSatisfaction",
	SSI: "customerSatisfaction",
	EBE: "ebe",
	SFE: "sfe",
	PASE: "pase",
	Pinnacle: "pinnacle",
};

const CADILLAC = "Cadillac";

function getLocationPrograms(OEMs = []) {
	const results = OEMs?.reduce((acc, prev) => {
		const localPrograms = prev.financialStatement?.franchises?.reduce(
			(programs, franchise) => [...programs, ...franchise.programs],
			[]
		);
		return [...acc, ...localPrograms];
	}, []);
	return [...new Map(results.map((item) => [`${item.program}`, item])).values()];
}

// Receive an object of programs keys
function getRemovePrograms(programs = null) {
	const remove = [];
	if (programs) {
		for (const [key, value] of Object.entries(programs)) {
			if (key && !value) {
				remove.push(key);
			}
		}
	}
	return [...new Set(remove)];
}

function getSectionsPrograms(locationPrograms = [], programs = []) {
	return programs.reduce(
		(acc, program) => ({
			...acc,
			[program]: locationPrograms.some((item) => item?.program?.toUpperCase() === program?.toUpperCase()),
		}),
		{}
	);
}

function useLocationPrograms(OEMs = []) {
	const locationPrograms = useMemo(() => getLocationPrograms(OEMs), [OEMs]);

	const isLocationProgram = useCallback(
		(program = "") => locationPrograms.some((item) => item?.toUpperCase() === program?.toUpperCase()),
		[locationPrograms]
	);

	const hasLocationPrograms = useCallback(
		(programs = []) => locationPrograms.every((item) => programs?.includes(item)),
		[locationPrograms]
	);

	return {
		hasLocationPrograms,
		isLocationProgram,
	};
}

const isFranchiseInGroup = (groups = [], franchise = "") => groups.some((item) => franchise === item?.franchiseName?.toUpperCase());

const getLocalFranchise = (franchises = [], franchise = null) => {
	if (!franchise) {
		return null;
	}

	const { franchise: localFranchise = "", groups = [], isCombined = false } = franchise || {};

	return franchises.find((item) => {
		const financialFranchise = item?.franchise?.toUpperCase();
		if (!isCombined) {
			return financialFranchise === localFranchise.toUpperCase();
		}
		return isFranchiseInGroup(groups, financialFranchise);
	});
};

const getFranchisePrograms = (OEMs = [], franchise = null) => {
	if (!franchise) {
		return [];
	}

	const results = OEMs?.reduce((acc, oem) => {
		const localFranchise = getLocalFranchise(oem?.financialStatement?.franchises || [], franchise);
		if (!localFranchise) {
			return acc;
		}
		const localPrograms = localFranchise?.programs || [];
		return [...acc, ...localPrograms];
	}, []);

	return [...new Map(results.map((item) => [`${item.program}`, item])).values()];
};

const isCSIProgram = (program = "") => ["CSI", "SSI"].includes(program);
const isCSIMandatoryDisabled = (franchises = [], program = "") => {
	if (!isCSIProgram(program)) {
		return false;
	}
	const compare = CADILLAC?.toUpperCase();
	return franchises?.every((item) => item?.franchise?.toUpperCase() === compare);
};

const getFranchisesPrograms = (OEMs = [], franchises = []) => {
	const results = franchises?.reduce((acc, franchise) => {
		const localPrograms = getFranchisePrograms(OEMs, franchise);
		return [...acc, ...localPrograms];
	}, []);
	return [...new Map(results.map((item) => [`${item.program}`, item])).values()];
};

const getProgramsUsage = (programs = [], localPrograms = [], franchises = []) => {
	const programsLabels = programs.map((item) => item.program);
	const localLabels = localPrograms.map((item) => item.program);

	const results = programsLabels.map((program) => ({
		program: PROGRAMS_ALIAS?.[program] || program,
		disabled: !localLabels.includes(program) || isCSIMandatoryDisabled(franchises, program),
	}));
	return [...new Map(results.map((item) => [`${item.program}`, item])).values()];
};

const getOEMFranchisePrograms = (OEMs = [], franchises = []) => {
	const locationPrograms = getLocationPrograms(OEMs);
	const franchisesPrograms = getFranchisesPrograms(OEMs, franchises);
	return getProgramsUsage(locationPrograms, franchisesPrograms, franchises);
};

const getOEMsFranchises = (OEMs = []) => OEMs?.reduce((acc, prev) => [...acc, ...prev.franchises], []);

const getExtendedBranchesFranchises = (branchesOEMs = [], branchesFranchises = []) => {
	const oemsFranchises = getOEMsFranchises(branchesOEMs);
	return branchesFranchises?.map((franchise) =>
		oemsFranchises.find((item) => item?.franchise?.toUpperCase() === franchise.toUpperCase())
	);
};

export {
	useLocationPrograms,
	getExtendedBranchesFranchises,
	getLocationPrograms,
	getOEMsFranchises,
	getOEMFranchisePrograms,
	getRemovePrograms,
	getSectionsPrograms,
};
